import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { Button, Icon } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import {
    closeInfoDialog,
    openInfoDialog,
    updateSavedSearchesThunk,
} from '../../reducers/save-search';
import { SrpState, SrpUseDispatch } from '../../reducers';
import { InfoDialog } from '../../info-dialog/info-dialog';
import {
    FullButtonLabel,
    saveSearchButtonCss,
    searchFiltersContainerCss,
    ShortButtonLabel,
} from './save-search.style';

type SaveSearchProps = {
    isNoSearchResults?: boolean;
};

export const SaveSearch = ({ isNoSearchResults = false }: SaveSearchProps) => {
    const dispatch = useDispatch() as SrpUseDispatch;
    const theme = useTheme();
    const { userLoggedIn, formState, token, hasSearchBeenSaved, showInfoDialog, isLoading } =
        useSelector(
            ({
                saveSearch: { hasSearchBeenSaved, formState, showInfoDialog, isLoading },
                userSavedAds,
                userData,
            }: SrpState) => ({
                hasSearchBeenSaved,
                formState,
                showInfoDialog,
                isLoading,
                token: userSavedAds.token,
                userLoggedIn: userData && userData.userLoggedIn,
            })
        );

    const saveSearchButtonText = useMemo(() => {
        if (hasSearchBeenSaved)
            return (
                <>
                    <FullButtonLabel>Search alert saved</FullButtonLabel>
                    <ShortButtonLabel>Search saved</ShortButtonLabel>
                </>
            );

        return (
            <>
                <FullButtonLabel>Save search alert</FullButtonLabel>
                <ShortButtonLabel>Save Search</ShortButtonLabel>
            </>
        );
    }, [hasSearchBeenSaved, isLoading]);

    const buttonIcon =
        !isLoading && (hasSearchBeenSaved ? <Icon type="bell-tick" /> : <Icon type="bell-plus" />);

    const onDialogCloseClick = () => {
        dispatch(closeInfoDialog());
        trackV2('SRP-savesearch-close');
    };

    const onSaveSearchButtonClick = () => {
        trackV2('SRP-savesearch-open');
        trackPermutiveCustomEvent(window, PermutiveEventNames.SRP_SAVE_SEARCH_OPEN, {});

        if (!userLoggedIn) {
            dispatch(openInfoDialog('NOT_LOGGED_IN'));
            trackV2('SRP-savesearch-notloggedin');
        } else {
            if (!hasSearchBeenSaved) {
                formState && dispatch(updateSavedSearchesThunk(formState, token));
            } else {
                dispatch(openInfoDialog('CONFIRMED'));
            }
        }
    };

    return (
        <div css={searchFiltersContainerCss(theme, isNoSearchResults)}>
            <InfoDialog isDialogOpened={showInfoDialog} onDialogCloseClick={onDialogCloseClick} />
            <Button
                {...qaAttribute('save-search-btn')}
                display="secondary"
                label={saveSearchButtonText}
                icon={buttonIcon}
                onClick={onSaveSearchButtonClick}
                isLoading={isLoading}
                className="save-search-btn"
                css={saveSearchButtonCss(theme, hasSearchBeenSaved, isNoSearchResults)}
            />
        </div>
    );
};
